import * as React from 'react';
import {observer} from 'mobx-react';
import {Modal, ModalChildStyle} from './modal';
import {em, important, px} from 'csx';
import {colorBackground, colorSlate, colorTwitter, colorWhite} from '../theme/color';
import {TextNoOrphan} from './text-no-orphan';
import {classes, style} from 'typestyle';
import {PillStyle, PillStyleDefault, PillStyleDisabled} from './pill';
import {SVGIcon} from './svg-icon';
import {FormInputCheckbox} from './form-input-checkbox';

export interface OnboardingTwitterModalModel {
  show: boolean;
  twitterLoginURL: string;
  saving: boolean;
  follow: boolean;
  isOnboarding: boolean;
}

export interface OnboardingTwitterModalProps {
  model: OnboardingTwitterModalModel;
  onRequestClose(): void;
  onFollowUs(follow: boolean): void;
  onSkip(): void;
  i18n?: {
    title: string;
    twitter: string;
    skip: string;
    close: string;
    skipping: string;
    content: string;
    caveat: string;
    follow: string;
  }
}

const DEFAULT_I18N = {
  title: 'Link your Twitter account',
  twitter: 'Continue with Twitter',
  skip: 'Skip for now',
  close: 'Close',
  skipping: 'Skipping...',
  content: 'Help us to help you! Tell other creators about your experience and help us raise the game and get even better content on Lickd.',
  caveat: 'You can update your notification settings from My Account at any time.',
  follow: 'Follow us on @GetLickd'
};

@observer
export class OnboardingTwitterModal extends React.Component<OnboardingTwitterModalProps, any> {
  render() {
    const { model, onRequestClose, onSkip, i18n = DEFAULT_I18N, onFollowUs } = this.props;

    const onClick = model.isOnboarding ? onSkip : onRequestClose;

    return (
      <Modal show={model.show} onRequestClose={onRequestClose}>
        {model.show && (
          <div className={OnboardingTwitterModal.styles.container}>
            <h3>
              <TextNoOrphan value={i18n.title}/>
            </h3>
            <p>{i18n.content}</p>
            <p>{i18n.caveat}</p>
            <div>
              <FormInputCheckbox
                name="follow"
                id="follow-us"
                value={model.follow}
                placeholder={i18n.follow}
                onChange={(_, follow) => onFollowUs(follow)}
              />
            </div>
            {!model.saving && (
              <a className={classes(OnboardingTwitterModal.styles.link, OnboardingTwitterModal.styles.linkContinue)} href={model.twitterLoginURL}>
                <SVGIcon.Twitter color={colorWhite.toString()} />
                <span>{i18n.twitter}</span>
              </a>
            )}
            <button className={classes(OnboardingTwitterModal.styles.link, OnboardingTwitterModal.styles.linkCancel)} disabled={model.saving} onClick={onClick}>
              <span>{model.saving ? i18n.skipping : (model.isOnboarding ? i18n.skip : i18n.close)}</span>
            </button>
          </div>
        )}
      </Modal>
    )
  }

  static styles = {
    container: style({
      ...ModalChildStyle,
      width: px(420),
      maxWidth: 'calc(100vw - 4rem)',
      background: colorWhite.toString(),
      $nest: {
        '&> h3': {
          color: colorTwitter.toString(),
          fontSize: px(30),
          margin: '0 0 30px 0',
        },
        '& p': {
          color: colorSlate.toString(),
          margin: '5px 0 20px 0',
          lineHeight: em(1.4)
        }
      }
    }),
    link: style({
      ...PillStyle,
      ...PillStyleDefault,
      margin: '10px 0 0 0',
      padding: '0.6em',
      lineHeight: px(24),
    }),
    linkContinue: style({
      background: colorTwitter.toString(),
      $nest: {
        '&:hover': {
          color: colorWhite.toString(),
          background: important(colorTwitter.darken(0.15).toString())
        },
        '& span': {
          flex: 1,
          textAlign: 'center'
        }
      }
    }),
    linkCancel: style({
      color: colorSlate.toString(),
      background: colorBackground.toString(),
      $nest: {
        '&:hover': {
          color: colorSlate.toString(),
          background: important(colorBackground.darken(0.15).toString()),
        },
        '& span': {
          flex: 1,
          textAlign: 'center'
        },
        '&:disabled': {
          ...PillStyleDisabled
        }
      }
    })
  }
}